import { DiscountValueQualifier, OfferProposal } from './types';

export const ProposalBenefitType = {
  PERCENT: 'percent',
  AMOUNT: 'amount',
  OTHER: 'other',
} as const;

export type ProposalBenefitType =
  typeof ProposalBenefitType[keyof typeof ProposalBenefitType];

const CURRENCY_SYMBOLS = {
  USD: '$',
  GBP: '£',
  EUR: '€',
} as const;

export type SupportedProposalCurrency = keyof typeof CURRENCY_SYMBOLS;

export interface ProposalBenefit {
  type: ProposalBenefitType;
  amount: string;
  qualifier: DiscountValueQualifier;
  currency?: SupportedProposalCurrency;
  freeform: string;
}

const qualifierFromValue = (value: string): DiscountValueQualifier =>
  /\b(up to|as much as)\b/i.test(value)
    ? DiscountValueQualifier.UP_TO
    : DiscountValueQualifier.EXACTLY;

export const benefitFromProposal = (
  proposal: OfferProposal,
): ProposalBenefit => {
  const qualifier = qualifierFromValue(proposal.value);
  const hasCompoundReward =
    /referral/i.test(proposal.offerKind) ||
    /\bgive\b.*\bget\b/i.test(proposal.value);

  if (
    !hasCompoundReward &&
    proposal.canonicalKind === 'percent_off' &&
    proposal.valuePercent !== null &&
    proposal.valuePercent !== undefined
  ) {
    return {
      type: ProposalBenefitType.PERCENT,
      amount: proposal.valuePercent,
      qualifier,
      freeform: proposal.value,
    };
  }

  const currency = proposal.valueCurrency?.toUpperCase();
  if (
    !hasCompoundReward &&
    proposal.canonicalKind === 'amount_off' &&
    proposal.valueAmountCents !== null &&
    proposal.valueAmountCents !== undefined &&
    currency &&
    currency in CURRENCY_SYMBOLS
  ) {
    return {
      type: ProposalBenefitType.AMOUNT,
      amount: (proposal.valueAmountCents / 100).toString(),
      qualifier,
      currency: currency as SupportedProposalCurrency,
      freeform: proposal.value,
    };
  }

  return {
    type: ProposalBenefitType.OTHER,
    amount: '',
    qualifier,
    freeform: proposal.value,
  };
};

export const formatProposalBenefit = (benefit: ProposalBenefit): string => {
  if (benefit.type === ProposalBenefitType.OTHER) return benefit.freeform;

  const canonicalAmount = benefit.amount.startsWith('.')
    ? `0${benefit.amount}`
    : benefit.amount;
  const qualifier =
    benefit.qualifier === DiscountValueQualifier.UP_TO ? 'Up to' : 'Exactly';
  if (benefit.type === ProposalBenefitType.PERCENT) {
    return `${qualifier} ${canonicalAmount}% off`;
  }

  if (!benefit.currency) return benefit.freeform;
  return `${qualifier} ${
    CURRENCY_SYMBOLS[benefit.currency]
  }${canonicalAmount} off`;
};
